import { z } from 'zod';

/**
 * F13 venue↔trainer affiliation types + Zod schemas.
 *
 * Decisions: see plans/13_F13_staff_permissions_and_trainer_affiliation.md
 * (Decisions 2, 5, 7, 8, 9, 11).
 *
 * ─── Symmetric model ─────────────────────────────────────────────────────────
 * The SAME row models both directions (venue-initiated and trainer-initiated),
 * distinguished by `initiatedBy` plus the initial `status`:
 *   - venue calls `inviteTrainer()`  → status='invited'   + initiatedBy='venue'
 *   - trainer calls `requestAffiliation()` → status='requested' + initiatedBy='trainer'
 *
 * Whichever side is NOT the initiator can `accept` or `decline`; the initiator
 * can `withdraw` while pending. Status lifecycle:
 *
 *   (no row) → invited                — venue invites
 *   (no row) → requested              — trainer requests
 *   invited  → active                 — trainer accepts
 *   invited  → declined  (terminal)   — trainer declines
 *   invited  → withdrawn (terminal)   — venue withdraws
 *   requested → active                — venue accepts
 *   requested → declined  (terminal)  — venue declines
 *   requested → withdrawn (terminal)  — trainer withdraws
 *   active    → suspended             — either side; future bookings cancelled,
 *                                       both visibility flags forced false
 *   suspended → active                — either side reactivates; visibility
 *                                       flags stay false until manually toggled
 *   suspended → ended (terminal)      — either side ends
 *   active    → ended (terminal)      — either side ends; same booking cancel
 *
 * The DB partial unique index `venue_trainer_affiliations_open_per_pair`
 * enforces "one open row per (venue, trainer)" so terminal rows accumulate
 * as history.
 *
 * ─── Visibility model (Decision 8) ───────────────────────────────────────────
 * Two independent boolean flags — BOTH must be true for the affiliation to
 * appear on either public surface:
 *   - `trainerListedOnVenuePage`  → venue-side toggle (owner/manager controls)
 *   - `venueListedOnTrainerPage`  → trainer-side toggle (trainer controls)
 *
 * On suspend/end, BOTH flags auto-flip to false. On reactivate, they stay
 * false until manually re-toggled (defence-in-depth — re-listing is explicit).
 *
 * ─── Booking mode (Decision 9) ───────────────────────────────────────────────
 * Per-affiliation snapshot read by F16 when ranging slot availability:
 *   - `venue_calendar`   → trainer's sessions consume venue calendar slots
 *                          (default for `type='employed'|'freelance'`)
 *   - `trainer_calendar` → trainer manages a parallel calendar; venue calendar
 *                          shows soft-reservation only (default for `type='guest'`)
 *
 * Mode changes do NOT retroactively affect existing future bookings (they
 * snapshot the mode at booking time, mirroring F05's `fx_rate_id` pattern).
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

export const VENUE_TRAINER_AFFILIATION_STATUSES = [
  'invited',
  'requested',
  'active',
  'suspended',
  'ended',
  'declined',
  'withdrawn',
] as const;
export type VenueTrainerAffiliationStatus = (typeof VENUE_TRAINER_AFFILIATION_STATUSES)[number];

export const VENUE_TRAINER_AFFILIATION_TYPES = ['employed', 'freelance', 'guest'] as const;
export type VenueTrainerAffiliationType = (typeof VENUE_TRAINER_AFFILIATION_TYPES)[number];

export const VENUE_TRAINER_AFFILIATION_INITIATED_BY = ['venue', 'trainer'] as const;
export type VenueTrainerAffiliationInitiatedBy =
  (typeof VENUE_TRAINER_AFFILIATION_INITIATED_BY)[number];

export const VENUE_TRAINER_AFFILIATION_BOOKING_MODES = [
  'venue_calendar',
  'trainer_calendar',
] as const;
export type VenueTrainerAffiliationBookingMode =
  (typeof VENUE_TRAINER_AFFILIATION_BOOKING_MODES)[number];

/**
 * Discriminator for `setVisibility()` — which side is toggling its own flag.
 * The endpoint authZ ensures only the correct side can flip its own flag.
 */
export const AFFILIATION_VISIBILITY_SIDES = ['venue', 'trainer'] as const;
export type AffiliationVisibilitySide = (typeof AFFILIATION_VISIBILITY_SIDES)[number];

export const AFFILIATION_NOTES_MAX_LENGTH = 500;
export const AFFILIATION_DECLINE_REASON_MAX_LENGTH = 500;
export const AFFILIATION_SUSPEND_REASON_MAX_LENGTH = 500;
export const AFFILIATION_END_REASON_MAX_LENGTH = 500;

// ─── Raw DB row shape (mirrors schema.ts venueTrainerAffiliations) ───────────

export type VenueTrainerAffiliation = {
  readonly id: string;
  readonly venueId: string;
  /**
   * Denormalised from `venues.owner_b2b_account_id` at insert time. Nullable
   * to survive a later venue-ownership null (F11's `ON DELETE SET NULL`); no
   * FK declared to avoid cascade ambiguity with the venue→account chain.
   */
  readonly b2bAccountId: string | null;
  readonly trainerUserId: string;
  readonly status: VenueTrainerAffiliationStatus;
  readonly type: VenueTrainerAffiliationType;
  readonly initiatedBy: VenueTrainerAffiliationInitiatedBy;
  readonly bookingMode: VenueTrainerAffiliationBookingMode;
  readonly trainerListedOnVenuePage: boolean;
  readonly venueListedOnTrainerPage: boolean;
  readonly notesForVenue: string | null;
  readonly notesForTrainer: string | null;
  readonly inviteId: string | null;
  readonly acceptedAt: string | null;
  readonly suspendedAt: string | null;
  readonly endedAt: string | null;
  readonly declineReason: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

// ─── Zod schemas ─────────────────────────────────────────────────────────────

export const VenueTrainerAffiliationStatusSchema = z.enum(VENUE_TRAINER_AFFILIATION_STATUSES);
export const VenueTrainerAffiliationTypeSchema = z.enum(VENUE_TRAINER_AFFILIATION_TYPES);
export const VenueTrainerAffiliationInitiatedBySchema = z.enum(
  VENUE_TRAINER_AFFILIATION_INITIATED_BY,
);
export const VenueTrainerAffiliationBookingModeSchema = z.enum(
  VENUE_TRAINER_AFFILIATION_BOOKING_MODES,
);
export const AffiliationVisibilitySideSchema = z.enum(AFFILIATION_VISIBILITY_SIDES);

// ─── Input schemas ───────────────────────────────────────────────────────────

/**
 * Venue-initiated invite. The venue side (owner or scoped manager) picks a
 * trainer by user ID, the affiliation type, and the booking mode. Optional
 * `notesForTrainer` free-form text (500 chars).
 */
export const InviteTrainerAffiliationInputSchema = z
  .object({
    trainerUserId: z.string().uuid(),
    type: VenueTrainerAffiliationTypeSchema,
    bookingMode: VenueTrainerAffiliationBookingModeSchema,
    notesForTrainer: z.string().max(AFFILIATION_NOTES_MAX_LENGTH).optional(),
  })
  .strict();
export type InviteTrainerAffiliationInput = z.infer<typeof InviteTrainerAffiliationInputSchema>;

/**
 * Trainer-initiated request. The trainer picks a venue, type, and mode; the
 * optional `notesForVenue` is free-form (500 chars).
 */
export const RequestVenueAffiliationInputSchema = z
  .object({
    venueId: z.string().uuid(),
    type: VenueTrainerAffiliationTypeSchema,
    bookingMode: VenueTrainerAffiliationBookingModeSchema,
    notesForVenue: z.string().max(AFFILIATION_NOTES_MAX_LENGTH).optional(),
  })
  .strict();
export type RequestVenueAffiliationInput = z.infer<typeof RequestVenueAffiliationInputSchema>;

export const DeclineAffiliationInputSchema = z
  .object({
    reason: z.string().max(AFFILIATION_DECLINE_REASON_MAX_LENGTH).optional(),
  })
  .strict();
export type DeclineAffiliationInput = z.infer<typeof DeclineAffiliationInputSchema>;

export const SuspendAffiliationInputSchema = z
  .object({
    reason: z.string().max(AFFILIATION_SUSPEND_REASON_MAX_LENGTH).optional(),
  })
  .strict();
export type SuspendAffiliationInput = z.infer<typeof SuspendAffiliationInputSchema>;

export const EndAffiliationInputSchema = z
  .object({
    reason: z.string().max(AFFILIATION_END_REASON_MAX_LENGTH).optional(),
  })
  .strict();
export type EndAffiliationInput = z.infer<typeof EndAffiliationInputSchema>;

/**
 * Single visibility-flag toggle. Each side can only flip its own flag — the
 * service-layer authZ enforces "venue side can set `side='venue'`, trainer
 * side can set `side='trainer'`". The DB column written is determined by
 * `side`:
 *   - `side='venue'`   → updates `trainer_listed_on_venue_page`
 *   - `side='trainer'` → updates `venue_listed_on_trainer_page`
 */
export const SetAffiliationVisibilityInputSchema = z
  .object({
    side: AffiliationVisibilitySideSchema,
    value: z.boolean(),
  })
  .strict();
export type SetAffiliationVisibilityInput = z.infer<typeof SetAffiliationVisibilityInputSchema>;

/**
 * Booking-mode change. Allowed only while the affiliation is `active`. Future
 * existing bookings keep their snapshot mode; only new bookings inherit the
 * new mode.
 */
export const SetAffiliationBookingModeInputSchema = z
  .object({
    mode: VenueTrainerAffiliationBookingModeSchema,
  })
  .strict();
export type SetAffiliationBookingModeInput = z.infer<typeof SetAffiliationBookingModeInputSchema>;
